import * as React from "react";
import { NavLink } from "react-router-dom";
import TrimPlayerV2 from "../TrimPlayer/TrimPlayerV2";
import { cx } from "utils/join-classnames";
import { useFormValidation } from "ui/hooks/use-form-validation";
import {
    Fragment,
    MediaFile,
    AspectRatio,
    DropdownOption,
    Crop,
} from "core/models";
import { Modal, Switch, FormElement, Dropdown } from "ui/components";
import aspectRatioOptions from "core/constants/edits";
import { EditRequest } from "core/services/site/fragment";
import usePermissions from "ui/hooks/usePermissions";
import { Tooltip } from "ui/components/atoms";
import styles from "./EditModal.module.scss";

interface Props {
    fragment: Fragment;
    addons: MediaFile[];
    handleClose(): void;
    handleSave?: (editRequest: EditRequest) => void;
}

const EditModal: React.FC<Props> = ({
    fragment,
    handleClose,
    handleSave,
    addons,
}) => {
    const userPermissions = usePermissions();
    const [trimBegin, setTrimBegin] = React.useState<number>(0);
    const [trimEnd, setTrimEnd] = React.useState<number>(0);
    const [crop, setCrop] = React.useState<Crop>(new Crop());
    const [mode, setMode] = React.useState<AspectRatio | undefined>(undefined);
    const [sizeMode, setSizemode] = React.useState<"crop" | "resize">("crop");
    const [blurBackground, setBlurBackground] = React.useState<boolean>(false);
    const [blurCheckbox, setBlurCheckbox] = React.useState<boolean>(false);
    const [selectedIntro, setIntro] = React.useState<string | undefined>("");
    const [selectedOverlay, setOverlay] = React.useState<string | undefined>(
        "",
    );
    const [selectedOutro, setOutro] = React.useState<string | undefined>("");
    const [addonOptions, setAddonOptions] = React.useState<{
        intro: DropdownOption[];
        outro: DropdownOption[];
        overlay: DropdownOption[];
    }>({ intro: [], outro: [], overlay: [] });

    const userCanManageAddons = userPermissions.canManageAddons;
    const fragmentAspect = fragment.getAspectRatio();
    const mediaURL = fragment.getMediaURL();
    const thumbnailURL = fragment.getThumbnailURL();
    const isCropped = () => mode !== undefined;
    const isTrimmed = () => trimBegin !== 0 || trimEnd !== 0;
    const somethingChanged = () =>
        isTrimmed() ||
        isCropped() ||
        selectedIntro !== null ||
        selectedOutro !== null ||
        selectedOverlay !== null;

    const aspectRatioOptionsForEdit: DropdownOption[] = [
        {
            id: -1,
            title: "Original",
            iconName: "square-landscape",
            value: undefined,
        },
        ...aspectRatioOptions,
    ];

    const theForm = useFormValidation({
        aspect: {
            valid: true,
            touched: false,
            value: undefined,
            errorMsg: "",
            rules: {},
            options: aspectRatioOptionsForEdit,
        },
        editcurrent: {
            valid: true,
            touched: false,
            value: "false",
            errorMsg: "",
            rules: {},
        },
        blur: {
            valid: true,
            touched: false,
            value: "false",
            errorMsg: "",
            rules: {},
        },
    });

    // Build aspectRatio filtered addon options
    React.useEffect(() => {
        const defaultOption = {
            id: 0,
            title: "None (default)",
            value: "",
            iconName: undefined,
        };
        const emptyPlaceholder = {
            id: 0,
            title: "None for aspect ratio",
            value: "",
            iconName: undefined,
        };
        const aspectFilter = mode ?? fragmentAspect;
        const filteredAddons = addons.filter(
            (addon) => addon.getAspectRatio() === aspectFilter,
        );
        const newAddonOptions = {
            intro: [defaultOption],
            outro: [defaultOption],
            overlay: [defaultOption],
        };
        filteredAddons.map((mediaFile) => {
            const output = {
                id: mediaFile.id,
                title: mediaFile.name,
                value: mediaFile.key,
                iconName: undefined,
            };
            if (mediaFile.type === "Intro") {
                newAddonOptions.intro.push(output);
            }
            if (mediaFile.type === "Outro") {
                newAddonOptions.outro.push(output);
            }
            if (mediaFile.type === "Overlay") {
                newAddonOptions.overlay.push(output);
            }
            return mediaFile;
        });
        if (newAddonOptions.intro.length === 1) {
            newAddonOptions.intro = [emptyPlaceholder];
        }
        if (newAddonOptions.outro.length === 1) {
            newAddonOptions.outro = [emptyPlaceholder];
        }
        if (newAddonOptions.overlay.length === 1) {
            newAddonOptions.overlay = [emptyPlaceholder];
        }
        setAddonOptions(newAddonOptions);
    }, [addons, fragmentAspect, mode]);

    const onChange = (
        newTrimBegin: number,
        newTrimEnd: number,
        newCrop: Crop,
    ) => {
        setTrimBegin(newTrimBegin);
        setTrimEnd(newTrimEnd);
        setCrop(newCrop);
    };

    const clearState = () => {
        setCrop(new Crop());
        setMode(undefined);
        setIntro("");
        setOutro("");
        setOverlay("");
    };

    const onSwitchChange = (value: "crop" | "resize") => {
        setSizemode(value);
        if (value === "resize") {
            setBlurCheckbox(true);
        }
        if (value === "crop") {
            setBlurCheckbox(false);
        }
    };

    const onCheckboxChange = (value: string | string[] | File | undefined) => {
        if (value === "true") {
            theForm.inputChangeHandler("blur", value);
            setBlurBackground(true);
        } else {
            theForm.inputChangeHandler("blur", "false");
            setBlurBackground(false);
        }
    };

    const onDropdownValueChanged = (newValue?: string | string[] | File) => {
        clearState();
        if (typeof newValue !== "string") {
            return;
        }
        if (!newValue) {
            return;
        }
        setMode(newValue as AspectRatio);
    };

    const save = async () => {
        const newCrop = { ...crop };
        const normalizedStart = trimBegin - (trimBegin % 0.04);
        const normalizedEnd = trimEnd - (trimEnd % 0.04);

        const editRequest: EditRequest = {
            sourceFragmentId: fragment.id,
            trimStart: normalizedStart,
            trimEnd: normalizedEnd,
            targetWidth: fragment.getMediaDimensions().width,
            targetHeight: fragment.getMediaDimensions().height,
            usePadding: sizeMode === "resize",
            paddingBlurred: theForm.formState.blur.value === "true",
            cropSettings: [],
            updateSource: theForm.formState.editcurrent.value === "true",
        };
        if (selectedIntro) {
            editRequest.mediaFileIntroKey = selectedIntro;
        }
        if (selectedOutro) {
            editRequest.mediaFileOutroKey = selectedOutro;
        }
        if (selectedOverlay) {
            editRequest.mediaFileOverlayKey = selectedOverlay;
        }
        if (mode) {
            editRequest.targetWidth = newCrop.outputSize.width;
            editRequest.targetHeight = newCrop.outputSize.height;
        }
        if (mode && sizeMode === "crop") {
            editRequest.cropSettings = [
                {
                    start: 0.0,
                    cropLeft: newCrop.point.x,
                    cropTop: newCrop.point.y,
                    cropWidth: newCrop.outputSize.width,
                    cropHeight: newCrop.outputSize.height,
                },
            ];
        }

        if (handleSave) {
            handleSave(editRequest);
        }
    };
    const checkIfCropped = (): boolean => {
        let check = false;

        fragment.properties.forEach((prop) => {
            if (
                typeof prop.value === "object" &&
                "cropped" in prop.value &&
                prop.value.cropped
            ) {
                check = true;
            }
            if (
                typeof prop.value === "object" &&
                "padded" in prop.value &&
                prop.value.padded
            ) {
                check = true;
            }
        });
        return check;
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        switch (event.code) {
            case "Enter":
                if (somethingChanged()) {
                    save();
                }
                break;
            case "Escape":
                handleClose();
                break;
            default:
                break;
        }
    };
    React.useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    });

    const modalContent = (
        <div className={styles.content}>
            <div className={styles.content__left}>
                {mediaURL && (
                    <TrimPlayerV2
                        autoplay
                        trimMode={mode}
                        sizeMode={sizeMode}
                        videoUrl={mediaURL}
                        thumbnailUrl={thumbnailURL}
                        initialOffsetStart={0}
                        initialOffsetEnd={0}
                        onChange={onChange}
                        blur={blurBackground}
                    />
                )}
            </div>
            <div className={styles.content__right}>
                <div className={styles.title}>Editing</div>
                <FormElement
                    id="aspectratio"
                    theme="dark"
                    onChange={onDropdownValueChanged}
                    type="dropdown"
                    title="Aspect ratio"
                    fieldState={theForm.formState.aspect}
                    options={theForm.formState.aspect.options}
                    disabled={checkIfCropped()}
                />
                <div className={styles.switch_tooltip}>
                    <Switch
                        activeOption={sizeMode}
                        options={[
                            { icon: "crop", tooltip: "Crop", value: "crop" },
                            {
                                icon: "resize",
                                tooltip: "Resize",
                                value: "resize",
                            },
                        ]}
                        handleChange={onSwitchChange}
                        textOnly
                        disabled={mode === undefined}
                    />
                    {blurCheckbox && (
                        <div className={styles.checkbox}>
                            <Tooltip text="This may cause longer processing time">
                                <FormElement
                                    theme="dark"
                                    type="checkbox"
                                    fieldState={theForm.formState.blur}
                                    id="blur"
                                    title="Add blur"
                                    onChange={onCheckboxChange}
                                />
                            </Tooltip>
                        </div>
                    )}
                </div>
                <div className={styles.title}>Personalisation</div>
                {addonOptions.intro && addonOptions.intro.length >= 0 && (
                    <div className={cx(styles.formelement, styles.dropdown)}>
                        <div className={styles.formelement__caption}>
                            Intro {mode && `(${mode})`}
                        </div>
                        <Dropdown
                            theme="dark"
                            options={addonOptions.intro}
                            onValueChanged={setIntro}
                            value={selectedIntro}
                            disabled={addonOptions.intro.length <= 1}
                        />
                    </div>
                )}
                {addonOptions.overlay && addonOptions.overlay.length >= 0 && (
                    <div className={cx(styles.formelement, styles.dropdown)}>
                        <div className={styles.formelement__caption}>
                            Overlay {mode && `(${mode})`}
                        </div>
                        <Dropdown
                            theme="dark"
                            options={addonOptions.overlay}
                            onValueChanged={setOverlay}
                            value={selectedOverlay}
                            disabled={addonOptions.overlay.length <= 1}
                        />
                    </div>
                )}
                {addonOptions.outro && addonOptions.outro.length >= 0 && (
                    <div className={styles.formelement}>
                        <div className={styles.formelement__caption}>
                            Outro {mode && `(${mode})`}
                        </div>
                        <Dropdown
                            theme="dark"
                            options={addonOptions.outro}
                            onValueChanged={setOutro}
                            value={selectedOutro}
                            disabled={addonOptions.outro.length <= 1}
                        />
                    </div>
                )}
                {userCanManageAddons && (
                    <div className={styles.description}>
                        Addons missing?
                        <br />
                        Manage them on the{" "}
                        <NavLink className={styles.link} to="/settings/addons">
                            settings page
                        </NavLink>
                    </div>
                )}
            </div>
        </div>
    );

    const editCurrent = (
        <div className={styles.editCurrent}>
            <Tooltip text="Making changes to this clip directly may disallow you from reusing it for different purposes.">
                <FormElement
                    theme="dark"
                    type="checkbox"
                    fieldState={theForm.formState.editcurrent}
                    id="editcurrent"
                    title="Edit current clip"
                    onChange={(value) =>
                        theForm.inputChangeHandler("editcurrent", value)
                    }
                    checkBoxPos="right"
                />
            </Tooltip>
        </div>
    );

    return (
        <Modal
            content={modalContent}
            lineColor="amber"
            theme="dark"
            onSave={save}
            onClose={handleClose}
            saveButtonDisabled={!somethingChanged()}
            fullSize
            title={`Edit Clip ${fragment.title}`}
            icon="edit"
            saveButtonText="Create"
            extraButton={editCurrent}
        />
    );
};

export default EditModal;
